import { createApi } from "@reduxjs/toolkit/query/react";

import { VisibilityType } from "../../../types/VisibilityType";
import { CalendarType } from "../../../types/CalendarType";
import { ArrayItem } from "./types";
import { createServiceBaseQuery } from "../../../../../store/client/serviceBaseQuery";

export interface ItemTypeDTO {
  id: string;
  name: string;
  slug: string;
  description: string;
  accountId: number;
  color?: string;
  attributeDefinitions?: ArrayItem[];
  visibilityType: VisibilityType;
  createdAt: string;
  updatedAt: string;
}

export interface ItemTypeCreateDTO {
  name: string;
  description: string;
  color?: string;
}

export interface ItemDTO {
  id: string;
  calendarId: string;
  title: string;
  description: string;
  itemType?: ItemTypeDTO;
  itemTypeId: string;
  startDate?: string; // YYYY-MM-DD
  endDate?: string; // YYYY-MM-DD
  startDateTime?: string; // YYYY-MM-DDTHH:MM:SSZ
  endDateTime?: string; // YYYY-MM-DDTHH:MM:SSZ
  location?: string | null;
  rrule?: string | null;
  exdate?: string | null;
  customAttributes?: Record<string, { value: any; visible: boolean }>;
  createdAt: string;
  updatedAt: string;
}

export type ItemCreate = Omit<ItemDTO, "id" | "createdAt" | "updatedAt">;
export type ItemUpdate = Omit<ItemDTO, "createdAt" | "updatedAt">;

export const itemService = createApi({
  reducerPath: "itemService",
  baseQuery: createServiceBaseQuery("zeusUri"),
  tagTypes: ["Item"],
  endpoints: (builder) => ({
    getItems: builder.query<ItemDTO[], { calendarIds?: string[] }>({
      query: (arg) => {
        const { calendarIds } = arg;
        const params = new URLSearchParams();
        if (calendarIds && calendarIds.length > 0) {
          calendarIds.forEach((id) => params.append("calendarIds", id));
        }
        return {
          url: `/api/items${params.toString() ? `?${params.toString()}` : ""}`,
          method: "GET",
        };
      },
      providesTags: [{ type: "Item", id: "LIST" }],
    }),
    getItem: builder.query<ItemDTO, string>({
      query: (id) => ({
        url: `/api/items/${id}`,
        method: "GET",
      }),
      providesTags: (_result, _error, id) => [{ type: "Item", id }],
    }),
    createItem: builder.mutation<ItemDTO, ItemCreate>({
      query: (item) => ({
        url: `/api/items`,
        method: "POST",
        body: {
          ...item,
        },
      }),
      invalidatesTags: [{ type: "Item", id: "LIST" }],
    }),
    updateItem: builder.mutation<ItemDTO, { id: string; item: ItemUpdate }>({
      query: ({ id, item }) => ({
        url: `/api/items/${id}`,
        method: "PUT",
        body: {
          ...item,
        },
      }),
      invalidatesTags: (_result, _error, { id }) => [
        { type: "Item", id: "LIST" },
        { type: "Item", id },
      ],
    }),
    deleteItem: builder.mutation<void, string>({
      query: (id) => ({
        url: `/api/items/${id}`,
        method: "DELETE",
      }),
      invalidatesTags: [{ type: "Item", id: "LIST" }],
    }),
  }),
});

export interface CalendarDTO {
  id: string;
  name: string;
  description: string;
  accountId: number;
  color?: string;
  shortCode?: string;
  dxLocationId?: number | null;
  visibilityType: VisibilityType;
  calendarType: CalendarType;
  createdAt: string;
  updatedAt: string;
}

export interface CalendarCreateDTO {
  name: string;
  description: string;
  color?: string;
  dxLocationId?: number | null;
}

export interface CalendarShareDTO {
  accountIds: number[];
}

export const calendarService = createApi({
  reducerPath: "calendarService",
  baseQuery: createServiceBaseQuery("zeusUri"),
  tagTypes: ["Calendar"],
  endpoints: (builder) => ({
    getCalendar: builder.query<CalendarDTO[], void>({
      query: () => {
        return {
          url: `/api/calendars`,
          method: "GET",
        };
      },
      providesTags: [{ type: "Calendar", id: "LIST" }],
    }),
    createCalendar: builder.mutation<CalendarDTO, CalendarCreateDTO>({
      query: (calendar) => ({
        url: `/api/calendars`,
        method: "POST",
        body: calendar,
      }),
      invalidatesTags: [{ type: "Calendar", id: "LIST" }],
    }),
    updateCalendar: builder.mutation<CalendarDTO, CalendarDTO>({
      query: (calendar) => ({
        url: `/api/calendars/${calendar.id}`,
        method: "PUT",
        body: calendar,
      }),
      invalidatesTags: [{ type: "Calendar", id: "LIST" }],
    }),
    deleteCalendar: builder.mutation<void, string>({
      query: (id) => ({
        url: `/api/calendars/${id}`,
        method: "DELETE",
      }),
      invalidatesTags: [{ type: "Calendar", id: "LIST" }],
    }),
    getCalendarShares: builder.query<CalendarShareDTO, string>({
      query: (calendarId) => ({
        url: `/api/calendars/${calendarId}/shares`,
        method: "GET",
      }),
      providesTags: (_result, _error, calendarId) => [
        { type: "Calendar", id: `${calendarId}-shares` },
      ],
    }),
    updateCalendarShares: builder.mutation<
      CalendarShareDTO,
      { calendarId: string; accountIds: number[] }
    >({
      query: ({ calendarId, accountIds }) => ({
        url: `/api/calendars/${calendarId}/shares`,
        method: "POST",
        body: { accountIds },
      }),
      invalidatesTags: (_result, _error, { calendarId }) => [
        { type: "Calendar", id: `${calendarId}-shares` },
      ],
    }),
  }),
});

export interface VenueDTO {
  id: number;
  name: string;
}

export const venueService = createApi({
  reducerPath: "deVenueService",
  baseQuery: createServiceBaseQuery("aiakosUri"),
  tagTypes: ["Venue"],
  endpoints: (builder) => ({
    getVenues: builder.query<VenueDTO[], void>({
      query: () => ({
        url: `/api/venues`,
        method: "GET",
      }),
      providesTags: [{ type: "Venue", id: "LIST" }],
    }),
  }),
});

export const itemTypeService = createApi({
  reducerPath: "itemTypeService",
  baseQuery: createServiceBaseQuery("zeusUri"),
  tagTypes: ["ItemType"],
  endpoints: (builder) => ({
    getItemTypes: builder.query<ItemTypeDTO[], void>({
      query: () => ({
        url: `/api/item-types`,
        method: "GET",
      }),
      providesTags: [{ type: "ItemType", id: "LIST" }],
    }),
    getItemType: builder.query<ItemTypeDTO, string>({
      query: (id) => ({
        url: `/api/item-types/${id}`,
        method: "GET",
      }),
      providesTags: (_result, _error, id) => [{ type: "ItemType", id }],
    }),
    createItemType: builder.mutation<ItemTypeDTO, ItemTypeCreateDTO>({
      query: (itemType) => ({
        url: `/api/item-types`,
        method: "POST",
        body: itemType,
      }),
      invalidatesTags: [{ type: "ItemType", id: "LIST" }],
    }),
    updateItemType: builder.mutation<ItemTypeDTO, ItemTypeDTO>({
      query: (itemType) => ({
        url: `/api/item-types/${itemType.id}`,
        method: "PUT",
        body: itemType,
      }),
      invalidatesTags: [{ type: "ItemType", id: "LIST" }],
    }),
    deleteItemType: builder.mutation<void, string>({
      query: (id) => ({
        url: `/api/item-types/${id}`,
        method: "DELETE",
      }),
      invalidatesTags: [{ type: "ItemType", id: "LIST" }],
    }),
  }),
});

export const {
  useGetCalendarQuery,
  useCreateCalendarMutation,
  useUpdateCalendarMutation,
  useDeleteCalendarMutation,
  useGetCalendarSharesQuery,
  useUpdateCalendarSharesMutation,
} = calendarService;
export const { useGetVenuesQuery } = venueService;
export const {
  useGetItemTypesQuery,
  useGetItemTypeQuery,
  useCreateItemTypeMutation,
  useUpdateItemTypeMutation,
  useDeleteItemTypeMutation,
} = itemTypeService;
export const {
  useGetItemsQuery,
  useGetItemQuery,
  useCreateItemMutation,
  useUpdateItemMutation,
  useDeleteItemMutation,
} = itemService;
